import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider } from "@/hooks/useAuth";
import ChatPage from "./pages/Index";
import InquiryDetail from "./pages/InquiryDetail";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ProfileSettings from "./pages/ProfileSettings";

import SupplierDashboard from "./pages/SupplierDashboard";
import SupplierInquiryDetail from "./pages/SupplierInquiryDetail";
import SupplierSettings from "./pages/SupplierSettings";
import CustomerDashboard from "./pages/CustomerDashboard";
import NotFound from "./pages/NotFound";
import LegalPage from "./pages/LegalPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminServiceDetail from "./pages/AdminServiceDetail";
import SupplierChatPage from "./pages/SupplierChatPage";
import { CookieConsent } from "./components/CookieConsent";


const queryClient = new QueryClient();

function isCustomDomain(): string | null {
  const h = window.location.hostname;
  if (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h === "cenik.ai" ||
    h === "www.cenik.ai" ||
    h.endsWith(".cenik.ai") ||
    h.endsWith(".vercel.app")
  ) {
    return null;
  }
  return h;
}

const customDomain = isCustomDomain();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <I18nProvider>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <Sentry.ErrorBoundary fallback={<div className="flex items-center justify-center h-screen text-center p-8"><div><h1 className="text-2xl font-bold mb-2">Něco se pokazilo</h1><p className="text-muted-foreground mb-4">Zkus obnovit stránku.</p><button className="px-4 py-2 bg-primary text-white rounded" onClick={() => window.location.reload()}>Obnovit stránku</button></div></div>}>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={customDomain ? <SupplierChatPage domain={customDomain} /> : <ChatPage />} />
              <Route path="/chat" element={<Navigate to="/" replace />} />
              <Route path="/fasadnicke-prace" element={<Navigate to="/" replace />} />
              <Route path="/fasady" element={<Navigate to="/" replace />} />
              <Route path="/sdk" element={<Navigate to="/" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/profile" element={<ProfileSettings />} />
              <Route path="/supplier/register" element={<Login variant="supplier" />} />

              <Route path="/supplier/dashboard" element={<SupplierDashboard />} />
              <Route path="/supplier/inquiry/:inquiryId" element={<SupplierInquiryDetail />} />
              <Route path="/supplier/settings" element={<SupplierSettings />} />
              <Route path="/supplier/pricing" element={<Navigate to="/supplier/settings" replace />} />
              <Route path="/supplier/company" element={<Navigate to="/supplier/settings" replace />} />
              <Route path="/admin-secret" element={<AdminDashboard />} />
              <Route path="/admin-secret/service/:slug" element={<AdminServiceDetail />} />
              <Route path="/dashboard" element={<CustomerDashboard />} />
              <Route path="/dashboard/:inquiryId" element={<InquiryDetail />} />
              <Route path="/terms" element={<LegalPage contentKey="legal.termsResponse" />} />
              <Route path="/privacy" element={<LegalPage contentKey="legal.privacyResponse" />} />
              <Route path="/cookies" element={<LegalPage contentKey="legal.cookiesResponse" />} />
              <Route path="/about" element={<LegalPage contentKey="sidebar.aboutUsResponse" />} />
              <Route path="/:slug" element={<SupplierChatPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <CookieConsent />
          </BrowserRouter>
          </Sentry.ErrorBoundary>
        </TooltipProvider>
      </AuthProvider>
    </I18nProvider>
  </QueryClientProvider>
);

export default App;
